/**
 * React Query hooks for Timesheets module (P14)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TimeRecord {
  id: string;
  employeeId: string;
  employeeName?: string;
  branchId: string | null;
  date: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'exported';
  segments: WorkSegment[];
  breaks: BreakEntry[];
  computed: ComputedTime | null;
  weeklyContext: unknown;
  attestation: unknown;
  review: { flags?: AutoFlag[]; reviewedBy?: string; reviewedByName?: string; reviewedAt?: string; managerNotes?: string | null } | null;
  annotations: unknown[];
  mealBreakResolutions: unknown[];
  statusHistory: StatusChange[];
  createdAt: string;
  updatedAt: string;
}

export interface WorkSegment {
  id: string;
  clockIn: string;
  clockOut: string | null;
  clockInGps: GpsPoint | null;
  clockOutGps: GpsPoint | null;
  clockInMethod: string;
  clockOutMethod: string | null;
}

export interface GpsPoint {
  lat: number;
  lon: number;
  accuracy: number;
  capturedAt: string;
}

export interface BreakEntry {
  id: string;
  type: 'lunch' | 'paid_break';
  startTime: string;
  endTime: string | null;
  durationMinutes: number;
}

export interface ComputedTime {
  totalPaidMinutes: number;
  totalUnpaidMinutes: number;
  regularMinutes: number;
  overtimeMinutes: number;
  doubleOvertimeMinutes: number;
  breakCompliance: {
    mealBreakProvided: boolean;
    secondMealBreakRequired: boolean;
    secondMealBreakProvided: boolean;
    restBreaksRequired: number;
    restBreaksProvided: number;
    mealBreakPenalty: boolean;
    restBreakPenalty: boolean;
  };
}

export interface AutoFlag {
  type: string;
  severity: 'info' | 'warning' | 'critical';
  message: string;
}

export interface StatusChange {
  from: string;
  to: string;
  changedBy: string;
  changedByName: string;
  changedAt: string;
  reason: string | null;
}

export interface PayPeriod {
  periodStart: string;
  periodEnd: string;
  isCurrent: boolean;
  totalRecords: number;
  draft: number;
  submitted: number;
  approved: number;
  rejected: number;
  exported: number;
}

export interface ClockStatusData {
  clockedIn: { employeeId: string; employeeName: string; clockedInSince: string; gps: GpsPoint | null }[];
  total: number;
  date: string;
}

// ---------------------------------------------------------------------------
// Query Keys
// ---------------------------------------------------------------------------

export const timesheetKeys = {
  all: ['timesheets'] as const,
  my: (startDate?: string, endDate?: string) => [...timesheetKeys.all, 'my', { startDate, endDate }] as const,
  myDate: (date: string) => [...timesheetKeys.all, 'my', date] as const,
  team: (filters: Record<string, string | undefined>) => [...timesheetKeys.all, 'team', filters] as const,
  clockStatus: () => [...timesheetKeys.all, 'clock-status'] as const,
  payPeriods: () => [...timesheetKeys.all, 'pay-periods'] as const,
};

// ---------------------------------------------------------------------------
// Employee Hooks
// ---------------------------------------------------------------------------

export function useMyTimesheets(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: timesheetKeys.my(startDate, endDate),
    queryFn: async () => {
      const res = await api.timesheets.my({ startDate, endDate });
      if (!res.success) throw new Error(res.error?.message || 'Failed to fetch');
      return (res.data || []) as TimeRecord[];
    },
    staleTime: 30_000,
    gcTime: 10 * 60_000,
  });
}

export function useMyTimesheet(date: string) {
  return useQuery({
    queryKey: timesheetKeys.myDate(date),
    queryFn: async () => {
      const res = await api.timesheets.myDate(date);
      if (!res.success) throw new Error(res.error?.message || 'Failed to fetch');
      return res.data as TimeRecord | null;
    },
    staleTime: 10_000,
    gcTime: 5 * 60_000,
  });
}

export function useClockIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (gps?: GpsPoint) => {
      const res = await api.timesheets.clockIn(gps);
      if (!res.success) throw new Error(res.error?.message || 'Failed to clock in');
      return res.data as TimeRecord;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: timesheetKeys.all }),
  });
}

export function useClockOut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (gps?: GpsPoint) => {
      const res = await api.timesheets.clockOut(gps);
      if (!res.success) throw new Error(res.error?.message || 'Failed to clock out');
      return res.data as TimeRecord;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: timesheetKeys.all }),
  });
}

export function useBreakAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { action: 'start' | 'end'; type?: 'lunch' | 'paid_break' }) => {
      const res = await api.timesheets.breakAction(data);
      if (!res.success) throw new Error(res.error?.message || 'Failed');
      return res.data as TimeRecord;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: timesheetKeys.all }),
  });
}

export function useSubmitTimesheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (date: string) => {
      const res = await api.timesheets.submit(date);
      if (!res.success) throw new Error(res.error?.message || 'Failed to submit');
      return res.data as TimeRecord;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: timesheetKeys.all }),
  });
}

// ---------------------------------------------------------------------------
// Manager Hooks
// ---------------------------------------------------------------------------

export function useTeamTimesheets(filters: {
  startDate?: string;
  endDate?: string;
  status?: string;
  employeeId?: string;
}) {
  return useQuery({
    queryKey: timesheetKeys.team(filters),
    queryFn: async () => {
      const res = await api.timesheets.team(filters);
      if (!res.success) throw new Error(res.error?.message || 'Failed to fetch');
      return (res.data || []) as TimeRecord[];
    },
    staleTime: 30_000,
    enabled: !!filters.startDate && !!filters.endDate,
  });
}

export function useApproveTimesheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.timesheets.approve(id);
      if (!res.success) throw new Error(res.error?.message || 'Failed to approve');
      return res.data as TimeRecord;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: timesheetKeys.all }),
  });
}

export function useRejectTimesheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { id: string; notes?: string }) => {
      const res = await api.timesheets.reject(data.id, data.notes);
      if (!res.success) throw new Error(res.error?.message || 'Failed to reject');
      return res.data as TimeRecord;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: timesheetKeys.all }),
  });
}

export function useBulkApprove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { startDate: string; endDate: string }) => {
      const res = await api.timesheets.bulkApprove(data.startDate, data.endDate);
      if (!res.success) throw new Error(res.error?.message || 'Failed to bulk approve');
      return res.data!;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: timesheetKeys.all }),
  });
}

// ---------------------------------------------------------------------------
// Admin Hooks
// ---------------------------------------------------------------------------

export interface NotSubmittedEmployee {
  employeeId: string;
  employeeName: string;
}

export function useNotSubmitted(date?: string) {
  return useQuery({
    queryKey: [...timesheetKeys.all, 'not-submitted', date],
    queryFn: async () => {
      const res = await api.timesheets.notSubmitted(date);
      if (!res.success) throw new Error(res.error?.message || 'Failed to fetch');
      return (res.data || []) as NotSubmittedEmployee[];
    },
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

export function useRemind() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { employeeId: string; date: string }) => {
      const res = await api.timesheets.remind(data.employeeId, data.date);
      if (!res.success) throw new Error(res.error?.message || 'Failed to remind');
      return res.data as { reminded: boolean; recordId?: string; reason?: string };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: timesheetKeys.all }),
  });
}

export function useClockStatus() {
  return useQuery({
    queryKey: timesheetKeys.clockStatus(),
    queryFn: async () => {
      const res = await api.timesheets.clockStatus();
      if (!res.success) throw new Error(res.error?.message || 'Failed');
      return res.data as ClockStatusData;
    },
    staleTime: 15_000,
    gcTime: 5 * 60_000,
    refetchInterval: 60_000,
  });
}

export function usePayPeriods() {
  return useQuery({
    queryKey: timesheetKeys.payPeriods(),
    queryFn: async () => {
      const res = await api.timesheets.payPeriods();
      if (!res.success) throw new Error(res.error?.message || 'Failed');
      return (res.data || []) as PayPeriod[];
    },
    staleTime: 60_000,
    gcTime: 10 * 60_000,
  });
}

// ---------------------------------------------------------------------------
// Phase 2B: Manager Day Detail Hooks
// ---------------------------------------------------------------------------

export function useTeamDay(employeeId: string, date: string) {
  return useQuery({
    queryKey: [...timesheetKeys.all, 'team-day', employeeId, date],
    queryFn: async () => {
      const res = await api.timesheets.teamDay(employeeId, date);
      if (!res.success) throw new Error(res.error?.message || 'Failed');
      return res.data as TimeRecord & { employeeName: string };
    },
    staleTime: 15_000,
    gcTime: 5 * 60_000,
    enabled: !!employeeId && !!date,
  });
}

export function useEditClock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { id: string; segmentId: string; field: string; newTime: string }) => {
      const res = await api.timesheets.editClock(data.id, { segmentId: data.segmentId, field: data.field, newTime: data.newTime });
      if (!res.success) throw new Error(res.error?.message || 'Failed');
      return res.data as TimeRecord;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: timesheetKeys.all }),
  });
}

export function useAnnotate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { id: string; text: string; flagForPayroll?: boolean }) => {
      const res = await api.timesheets.annotate(data.id, { text: data.text, flagForPayroll: data.flagForPayroll });
      if (!res.success) throw new Error(res.error?.message || 'Failed');
      return res.data as TimeRecord;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: timesheetKeys.all }),
  });
}

// ---------------------------------------------------------------------------
// Phase 2C: Payroll Export Hooks
// ---------------------------------------------------------------------------

export function useExportPreview(periodStart: string, periodEnd: string) {
  return useQuery({
    queryKey: [...timesheetKeys.all, 'export-preview', periodStart, periodEnd],
    queryFn: async () => {
      const res = await api.timesheets.exportPreview(periodStart, periodEnd);
      if (!res.success) throw new Error(res.error?.message || 'Failed');
      return res.data;
    },
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    enabled: !!periodStart && !!periodEnd,
  });
}

export function useExportToQbo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { periodStart: string; periodEnd: string }) => {
      const res = await api.timesheets.exportToQbo(data.periodStart, data.periodEnd);
      if (!res.success) throw new Error(res.error?.message || 'Failed');
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: timesheetKeys.all }),
  });
}

export function useVoidExport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (exportId: string) => {
      const res = await api.timesheets.voidExport(exportId);
      if (!res.success) throw new Error(res.error?.message || 'Failed');
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: timesheetKeys.all }),
  });
}

export interface PayrollExportRecord {
  id: string;
  periodStart: string;
  periodEnd: string;
  exportedBy: string;
  exportedAt: string;
  status: 'pending' | 'success' | 'partial' | 'voided';
  recordCount: number;
  csvUrl: string | null;
}

export interface ExportPreviewEmployee {
  employeeId: string;
  employeeName: string;
  qboEmployeeId: string | null;
  days: number;
  regularMinutes: number;
  dailyOtMinutes: number;
  weeklyOtMinutes: number;
  doubleOtMinutes: number;
  mealBreakPenalties: number;
  restBreakPenalties: number;
  totalMinutes: number;
  payrollAnnotations: string[];
}

export interface ExportPreviewData {
  employees: ExportPreviewEmployee[];
  summary: {
    totalEmployees: number;
    totalRegularHours: number;
    totalOtHours: number;
    totalDtHours: number;
    totalPenalties: number;
    unapprovedCount: number;
    alreadyExportedCount: number;
  };
}

export function useExportHistory() {
  return useQuery({
    queryKey: [...timesheetKeys.all, 'export-history'],
    queryFn: async () => {
      const res = await api.timesheets.exportHistory();
      if (!res.success) throw new Error(res.error?.message || 'Failed');
      return (res.data || []) as PayrollExportRecord[];
    },
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

// ---------------------------------------------------------------------------
// Phase 2E: Holiday Hooks
// ---------------------------------------------------------------------------

export function useHolidays(year: number) {
  return useQuery({
    queryKey: [...timesheetKeys.all, 'holidays', year],
    queryFn: async () => {
      const res = await api.timesheets.holidays(year);
      if (!res.success) throw new Error(res.error?.message || 'Failed');
      return (res.data || []) as { id: string; date: string; name: string; paid: boolean; branchId: string | null }[];
    },
    staleTime: 60_000,
    gcTime: 10 * 60_000,
  });
}

export function useAddHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { date: string; name: string; paid?: boolean; branchId?: string }) => {
      const res = await api.timesheets.addHoliday(data);
      if (!res.success) throw new Error(res.error?.message || 'Failed');
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: timesheetKeys.all }),
  });
}

export function useDeleteHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.timesheets.deleteHoliday(id);
      if (!res.success) throw new Error(res.error?.message || 'Failed');
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: timesheetKeys.all }),
  });
}

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------

export function formatMinutes(mins: number): string {
  if (mins <= 0) return '0h';
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  if (m === 0) return `${h}h`;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

export function formatDecimalHours(mins: number): string {
  return (mins / 60).toFixed(2);
}
